/**
 * 体积工具插件 —— 通过拖拽方式定义体积盒，计算体积值。
 */
import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import {
  AsyncHoverPickCoalescer,
  prefersAsyncToolPicking,
} from '@/plugins/tools/shared/toolPickPolicy'
import {
  getActiveViewCamera,
  getOptionalViewRegistry,
  isActiveViewFrame,
} from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import type { InputConsumer, NormalizedPointerEvent } from '@/shared/types/core'
import type { ActivatableToolPlugin, ActivationToken } from '@/shared/types/plugin'
import type { InputRouter, PickingService, ViewRegistry } from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import type { VolumeResult } from './VolumeData'
import { buildVolumeResult } from './VolumeData'

/** hover 预热缓存有效期：超过后不再作为按下起点（避免用过期点开始拖拽）。 */
const VOLUME_HOVER_CACHE_TTL_MS = 1500
/** hover 预热缓存允许的指针位移：超过则视为换位置，需要重新预热。 */
const VOLUME_HOVER_CACHE_RADIUS_PX = 12

/** 体积工具插件，用户通过拖拽定义体积盒 */
export class VolumePlugin implements ActivatableToolPlugin {
  /** 插件名称 */
  readonly name = 'volume'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 50
  /** 所属互斥组 */
  readonly group = 'default'
  /** 依赖的服务名列表 */
  readonly dependencies = ['PickingService', 'InputRouter'] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['VolumeService'] as const

  private _active = false
  private activationToken: ActivationToken | null = null
  private context!: EngineContext
  private inputRouter!: InputRouter
  private pickingService!: PickingService
  private results: VolumeResult[] = []
  private currentCamera: THREE.Camera | null = null
  private viewRegistry: ViewRegistry | null = null
  private readonly handleViewActivated = (event: {
    viewId: string
    previousViewId: string | null
  }) => {
    this.currentCamera = this.resolveActiveViewCamera()
    if (event.previousViewId && event.previousViewId !== event.viewId) {
      this.cancelCurrentVolume()
    }
  }
  /** 是否正在拖拽 */
  private isDragging = false
  /** 拖拽中的异步拾取合并器（WebGPU）：单在途，避免逐帧同步拾取阻塞主线程。 */
  private readonly dragPick = new AsyncHoverPickCoalescer()
  /** hover 预热拾取合并器（WebGPU）：为按下瞬间准备命中点。 */
  private readonly hoverPick = new AsyncHoverPickCoalescer(60)
  private hoverCache: {
    point: THREE.Vector3
    screen: THREE.Vector2
    at: number
    cameraWorld: number[]
  } | null = null
  /** 是否处于「已接管按下、等待异步起点」状态。 */
  private pendingDragOrigin = false
  /** 当前正在创建的体积盒变换矩阵 */
  private currentMatrix: THREE.Matrix4 | null = null
  /** 当前正在创建的体积盒尺寸 */
  private currentDimensions: THREE.Vector3 | null = null

  /** 输入事件消费者 */
  private readonly consumer: InputConsumer = {
    name: 'volume',
    priority: 100,
    onPointerDown: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerDown(event)
    },
    onPointerMove: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerMove(event)
    },
    onPointerUp: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerUp(event)
    },
  }

  /** 是否处于激活状态 */
  get isActive(): boolean {
    return this._active
  }

  /**
   * 记录外部分配的激活令牌。
   *
   * @param token - 激活令牌
   */
  setActivationToken(token: ActivationToken): void {
    this.activationToken = token
  }

  /**
   * 初始化：存储引擎上下文，注册服务。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    context.registerService('VolumeService', this)
  }

  /** 启动插件，解析依赖并订阅视口事件 */
  onStart(): void {
    this.inputRouter = this.context.getService('InputRouter')
    this.pickingService = this.context.getService('PickingService')
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.currentCamera = this.resolveActiveViewCamera()
    if (this.viewRegistry) {
      this.context.events.on('view.activated', this.handleViewActivated)
    }
  }

  /**
   * 每帧同步活动视口相机。
   *
   * @param frame - 视图帧上下文
   */
  onRenderView(frame: ViewFrameContext): void {
    if (!this._active) return
    if (!isActiveViewFrame(this.viewRegistry, frame)) return
    this.currentCamera = this.resolveActiveViewCamera() ?? frame.camera
  }

  /** 销毁插件，取消订阅并停用工具 */
  onDestroy(): void {
    if (this.viewRegistry) {
      this.context.events.off('view.activated', this.handleViewActivated)
    }
    if (this._active && this.activationToken) this.deactivate(this.activationToken)
    this.viewRegistry = null
  }

  /**
   * 激活工具，注册输入消费者。
   *
   * @param token - 激活令牌
   */
  activate(token: ActivationToken): void {
    this.validateToken(token)
    if (this._active) return
    this._active = true
    this.inputRouter.addConsumer(this.consumer)
  }

  /**
   * 停用工具，移除输入消费者并完成当前拖拽。
   *
   * @param token - 激活令牌
   */
  deactivate(token: ActivationToken): void {
    this.validateToken(token)
    if (!this._active) return
    this._active = false
    this.inputRouter.removeConsumer(this.consumer)
    this.finishCurrentVolume()
    this.currentCamera = null
  }

  /**
   * 获取当前拖拽中的体积框状态（只读），供 Overlay 插件读取中间状态。
   *
   * @returns 克隆的矩阵与尺寸；未拖拽返回 null
   */
  getCurrentDrag(): { matrix: THREE.Matrix4; dimensions: THREE.Vector3 } | null {
    if (!this.isDragging || !this.currentMatrix || !this.currentDimensions) return null
    return { matrix: this.currentMatrix.clone(), dimensions: this.currentDimensions.clone() }
  }

  /**
   * 获取所有体积结果。
   *
   * @returns 体积结果数组
   */
  getResults(): ReadonlyArray<VolumeResult> {
    return this.results
  }

  /**
   * 根据 ID 移除指定体积结果。
   *
   * @param id - 体积结果 ID；不存在时忽略
   */
  removeResult(id: string): void {
    const prevLength = this.results.length
    this.results = this.results.filter((r) => r.id !== id)
    if (this.results.length !== prevLength) this.emitResultChanged()
  }

  /** 清空所有体积结果 */
  clearResults(): void {
    if (this.results.length === 0) return
    this.results = []
    this.emitResultChanged()
  }

  /** 处理指针按下：左键开始拖拽，右键取消 */
  private handlePointerDown(event: NormalizedPointerEvent): boolean {
    if (event.button === 2) {
      this.cancelCurrentVolume()
      return true
    }
    if (event.button !== 0) return false
    if (!this.currentCamera) return false

    if (prefersAsyncToolPicking(this.context.graphics)) {
      // WebGPU 下按下不能同步拾取（会走 CPU 整份 splat 遍历，重模型上可达秒级）。这里一律由工具
      // 接管本次按下：命中点优先用 hover 预热缓存（零成本），缓存不可用时用异步拾取补，拖动过程
      // 同样异步。代价是「体积盒激活时左键拖空白不再旋转相机」，与其它框选类工具语义一致。
      const cached = this.resolveCachedHit(event.screen)
      if (cached) {
        this.beginDrag(cached)
        return true
      }

      this.beginPendingDrag()
      const camera = this.currentCamera
      void this.pickingService
        .pickAtScreenAsync(event.screen, camera, { interactionType: 'click' })
        .then((results) => {
          if (!this.pendingDragOrigin || !this.isDragging || !this.currentCamera) {
            return
          }
          const hit = results[0]
          if (!hit) {
            this.cancelCurrentVolume()
            return
          }
          this.applyDragHit(hit.point)
        })
        .catch(() => {
          if (this.pendingDragOrigin) {
            this.cancelCurrentVolume()
          }
        })
      return true
    }

    const pickResults = this.pickingService.pickAtScreen(event.screen, this.currentCamera)
    if (pickResults.length > 0) {
      this.beginDrag(pickResults[0].point)
      return true
    }
    return false
  }

  /** 开始一次体积盒拖拽（同步/异步起点共用）。 */
  private beginDrag(hitPoint: THREE.Vector3): void {
    this.isDragging = true
    this.pendingDragOrigin = false
    this.currentMatrix = new THREE.Matrix4().setPosition(hitPoint)
    this.currentDimensions = new THREE.Vector3(0, 0, 0)
  }

  /** 先占位开始拖拽，等异步起点拾取回来再落位。 */
  private beginPendingDrag(): void {
    this.isDragging = true
    this.pendingDragOrigin = true
    this.currentMatrix = null
    this.currentDimensions = new THREE.Vector3(0, 0, 0)
  }

  /**
   * 取 hover 预热缓存中的命中点。
   *
   * 缓存由工具激活/悬停期间的异步拾取写入；只有「同一相机 + 同一 View + 指针没走远 + 足够新」
   * 才认为可用，避免把过期点当作起点。
   */
  private resolveCachedHit(screen: THREE.Vector2): THREE.Vector3 | null {
    const cache = this.hoverCache
    const camera = this.currentCamera
    if (!cache || !camera) {
      return null
    }
    if (performance.now() - cache.at > VOLUME_HOVER_CACHE_TTL_MS) {
      return null
    }
    if (screen.distanceTo(cache.screen) > VOLUME_HOVER_CACHE_RADIUS_PX) {
      return null
    }
    const cameraWorld = camera.matrixWorld.elements
    for (let index = 0; index < 16; index += 1) {
      if (Math.abs(cameraWorld[index] - cache.cameraWorld[index]) > 1e-6) {
        return null
      }
    }
    return cache.point.clone()
  }

  /** 记录一次 hover 预热命中；落空时清空缓存。 */
  private cacheHoverHit(screen: THREE.Vector2, point: THREE.Vector3 | null): void {
    const camera = this.currentCamera
    if (!point || !camera) {
      this.hoverCache = null
      return
    }
    this.hoverCache = {
      point: point.clone(),
      screen: screen.clone(),
      at: performance.now(),
      cameraWorld: Array.from(camera.matrixWorld.elements),
    }
  }

  /** 处理指针移动：拖拽时根据相机距离计算体积盒尺寸 */
  private handlePointerMove(event: NormalizedPointerEvent): boolean {
    if (!this.currentCamera) return false

    if (!this.isDragging) {
      // 非拖拽时做 hover 预热：为「按下即开始拖拽」准备好零成本命中点
      if (prefersAsyncToolPicking(this.context.graphics)) {
        const screen = event.screen.clone()
        this.hoverPick.schedule(async (token) => {
          const camera = this.currentCamera
          if (!camera || !this.hoverPick.isCurrent(token) || this.isDragging) {
            return
          }
          const results = await this.pickingService.pickAtScreenAsync(screen, camera, {
            interactionType: 'move',
          })
          if (!this.hoverPick.isCurrent(token) || this.isDragging) {
            return
          }
          this.cacheHoverHit(screen, results[0]?.point ?? null)
        })
      }
      return false
    }

    // WebGPU 的同步拾取会回退到 CPU 整份 splat 遍历（单次约 200ms）：拖拽期间每帧同步拾取会把
    // 主线程占满，因此拖拽中的采样走异步合并路径（单在途），落点稍晚但不再卡顿。
    if (prefersAsyncToolPicking(this.context.graphics)) {
      this.dragPick.schedule(async (token) => {
        if (!this.dragPick.isCurrent(token) || !this.isDragging || !this.currentCamera) {
          return
        }
        const results = await this.pickingService.pickAtScreenAsync(
          event.screen,
          this.currentCamera,
          { interactionType: 'move' }
        )
        if (!this.dragPick.isCurrent(token) || !this.isDragging || !this.currentCamera) {
          return
        }
        const hit = results[0]
        if (hit) {
          this.applyDragHit(hit.point)
        }
      })
      return true
    }

    const pickResults = this.pickingService.pickAtScreen(event.screen, this.currentCamera)
    if (pickResults.length === 0) return true

    const hitPoint = pickResults[0].point
    if (!this.currentMatrix) {
      this.currentMatrix = new THREE.Matrix4()
    }
    this.currentMatrix.setPosition(hitPoint)

    this.applyDragHit(hitPoint)

    return true
  }

  /** 用拖拽采样点更新体盒位置与尺寸（同步/异步共用）。 */
  private applyDragHit(hitPoint: THREE.Vector3): void {
    const camera = this.currentCamera
    if (!camera) {
      return
    }

    if (!this.currentMatrix) {
      this.currentMatrix = new THREE.Matrix4()
    }
    this.currentMatrix.setPosition(hitPoint)

    camera.updateMatrixWorld()
    const cameraSpacePoint = hitPoint.clone().applyMatrix4(camera.matrixWorld.clone().invert())
    const size = Math.abs(cameraSpacePoint.z / 5)
    this.currentDimensions = new THREE.Vector3(size, size, size)
  }

  /** 处理指针释放：完成体积盒创建 */
  private handlePointerUp(_event: NormalizedPointerEvent): boolean {
    if (!this.isDragging || !this.currentMatrix || !this.currentDimensions) return false

    // 确保尺寸非零才创建结果
    if (this.currentDimensions.lengthSq() > 0) {
      const result = buildVolumeResult(this.currentMatrix, this.currentDimensions)
      this.results.push(result)
      this.emitResultChanged()
    }

    this.resetDragState()
    return true
  }

  /** 取消当前拖拽中的体积盒 */
  private cancelCurrentVolume(): void {
    if (this.isDragging) {
      this.resetDragState()
      this.emitResultChanged()
    }
  }

  /** 重置拖拽状态 */
  private resetDragState(): void {
    this.dragPick.invalidate()
    this.hoverPick.invalidate()
    this.hoverCache = null
    this.pendingDragOrigin = false
    this.isDragging = false
    this.currentMatrix = null
    this.currentDimensions = null
  }

  /** 完成当前体积盒（deactivate 时调用） */
  private finishCurrentVolume(): void {
    if (
      this.isDragging &&
      this.currentMatrix &&
      this.currentDimensions &&
      this.currentDimensions.lengthSq() > 0
    ) {
      const result = buildVolumeResult(this.currentMatrix, this.currentDimensions)
      this.results.push(result)
    }
    this.resetDragState()
    this.emitResultChanged()
  }

  /** 发出结果变更事件 */
  private emitResultChanged(): void {
    this.context.events.emit('tool.resultChanged', { name: this.name, data: this.results })
  }

  /** 校验 ActivationToken 合法性 */
  private validateToken(token: ActivationToken): void {
    if (token !== this.activationToken)
      throw new Error('VolumePlugin: 非法的 ActivationToken，操作被拒绝')
  }
  private resolveActiveViewCamera(): THREE.Camera | null {
    return getActiveViewCamera(this.viewRegistry)
  }
}

export default VolumePlugin
